/**
 * L4 — shared helpers for portfolio matrix / summary views.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteComposite } from '../../types/portfolioComposite';
import type { PillarId } from './pillarRegistry';
import { PILLAR_IDS, PILLARS, isDecisionApplicablePillar } from './pillarRegistry';
import {
  pillarToDecisionScore,
  type DecisionBandColor,
  type DecisionBandId,
} from './decisionBands';

export type PillarRiskLevel = 'green' | 'amber' | 'red';

/** Map pillar benefit score → decision scale, then bucket for cell color. */
export function pillarRiskLevel(benefitScore: number): PillarRiskLevel {
  const decision = pillarToDecisionScore(benefitScore);
  if (decision <= 2.0) return 'green';
  if (decision <= 3.2) return 'amber';
  return 'red';
}

export function pillarRiskClasses(level: PillarRiskLevel): string {
  switch (level) {
    case 'green':
      return 'bg-success/15 text-success border-success/40';
    case 'amber':
      return 'bg-warning/15 text-warning border-warning/40';
    case 'red':
      return 'bg-danger/15 text-danger border-danger/40';
  }
}

export function decisionBandClasses(color: DecisionBandColor): string {
  switch (color) {
    case 'green':
      return 'bg-success/15 text-success border-success/40';
    case 'amber':
      return 'bg-warning/15 text-warning border-warning/40';
    case 'orange':
      return 'bg-orange-500/15 text-orange-600 border-orange-500/40 dark:text-orange-400';
    case 'red':
      return 'bg-danger/15 text-danger border-danger/40';
  }
}

export function bandColorForId(bandId: DecisionBandId): DecisionBandColor {
  switch (bandId) {
    case 'go':
      return 'green';
    case 'conditional':
      return 'amber';
    case 'conditionalHigh':
      return 'orange';
    case 'noGo':
      return 'red';
  }
}

/** † when composite confidence < 0.70 or any pillar confidence < 0.50. */
export function isTbcComposite(composite: SiteComposite): boolean {
  if (composite.confidence < 0.7) return true;
  return PILLAR_IDS.some(id => {
    // CONNECTIVITY-ADVISORY — an advisory pillar's confidence is excluded
    // from the composite's confidence mean, so it must not raise the † flag.
    const p = composite.pillars[id];
    if (!isDecisionApplicablePillar(id, p)) return false;
    return p && !p.missing && p.confidence < 0.5;
  });
}

export function siteTierLabel(site: SiteEntity): string {
  const tier = site.projectIntent?.datacenterTierTarget;
  if (tier) return `Tier ${tier}`;
  return '—';
}

const US_STATE_RE = /,\s*([A-Z]{2})(?:\s+\d|$|\s*,)/;

/** Best-effort region from site address (US state abbrev or trailing segment). */
export function siteRegionLabel(site: SiteEntity): string {
  const addr = site.address?.trim() ?? '';
  const m = addr.match(US_STATE_RE);
  if (m) return m[1];
  const parts = addr.split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1].slice(0, 24);
  return '—';
}

export interface PortfolioRiskHighlight {
  pillarId: PillarId;
  label: string;
  affectedSites: number;
  totalSites: number;
  pct: number;
}

const RISK_DECISION_THRESHOLD = 3.0;

/**
 * Pillars where ≥50% of sites score worse than 3.0 on the decision scale.
 * Returns up to 3, sorted by affected share descending.
 */
export function computeTopPortfolioRisks(
  composites: SiteComposite[],
  minShare = 0.5,
  limit = 3,
): PortfolioRiskHighlight[] {
  const totalSites = composites.length;
  if (totalSites === 0) return [];

  const counts = new Map<PillarId, number>();
  for (const composite of composites) {
    for (const pillarId of PILLAR_IDS) {
      // CONNECTIVITY-ADVISORY — advisory pillars cannot headline a portfolio
      // risk they are structurally unable to impose on any verdict.
      const p = composite.pillars[pillarId];
      if (!isDecisionApplicablePillar(pillarId, p)) continue;
      if (!p || p.missing) continue;
      const decision = pillarToDecisionScore(p.score);
      if (decision > RISK_DECISION_THRESHOLD) {
        counts.set(pillarId, (counts.get(pillarId) ?? 0) + 1);
      }
    }
  }

  return [...counts.entries()]
    .map(([pillarId, affectedSites]) => ({
      pillarId,
      label: PILLARS[pillarId].label,
      affectedSites,
      totalSites,
      pct: affectedSites / totalSites,
    }))
    .filter(r => r.pct >= minShare)
    .sort((a, b) => b.pct - a.pct || b.affectedSites - a.affectedSites)
    .slice(0, limit);
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}
